"use client"

import { useAccount, usePublicClient } from "wagmi"
import { isTMA } from "@telegram-apps/bridge"

import { encodeFunctionData } from "viem"
import axios from "axios"
import { hapticFeedback } from "@telegram-apps/sdk-react"
import { useTgContext } from "../telegram/TgRoot"
import { blessnet } from "../blessnet-chain-configs"
import { useMemo } from "react"
import { useWriteContract } from "./useWriteContract"
import { MIN_ETH_TO_PROCESS_AA_OPERATION } from "../telegram"
import { BLESSNET_API_URL } from "../../BLESSNET_API_URL"

/**
 * Yet another wrapper around useWriteContract.
 * This adds support to tg mini apps in a non-intrusive way.
 * In tg, sending txs is done by calling an API endpoint which then transfers
 * to the factory contract and finally makes the call.
 */
export function useWriteContractTg() {
  const wagmiWriteContract = useWriteContract()
  const client = usePublicClient()
  const { address } = useAccount()
  const { isSetup, balance } = useTgContext()
  const { setIsModalOpen } = useTgContext()
  if (isTMA("simple")) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(
      () => ({
        writeContractAsync: async (options: any) => {
          if (!isSetup) {
            setIsModalOpen(true)
            return
          }
          if (!balance.data || balance.data < MIN_ETH_TO_PROCESS_AA_OPERATION) {
            setIsModalOpen(true)
            return
          }
          const { chainId } = options
          const calldata = encodeFunctionData(options)
          const res = await axios.post(
            // does not match the proxied route
            `/api/tg/write-contract`,
            {
              account: address,
              chain: chainId === blessnet.id ? "mainnet" : "sepolia",
              calldata,
              target: options.address,
            },
          )
          hapticFeedback.notificationOccurred("success")

          const deliveryId = res.data.deliveryIds[0]
          let txHash = null
          let iteration = 0
          console.time(`delivery ${deliveryId}`)
          while (true) {
            if (iteration++ > 20) {
              throw new Error("Transaction not found")
            }
            let data
            try {
              ;({ data } = await axios.get(
                `${BLESSNET_API_URL}/deliveries/${deliveryId}`,
              ))
            } catch (e) {
              console.error(e)
            }
            const { status, errorDetails } = data
            if (status === "delivered") {
              txHash = data.deliveryTxnHash
              break
            }
            if (status === "failed") {
              throw new Error(
                errorDetails?.shortMessage || "Transaction failed",
              )
            }
            await new Promise((resolve) => setTimeout(resolve, 1000))
          }
          console.timeEnd(`delivery ${deliveryId}`)
          console.time(`txHash ${txHash}`)
          await client?.waitForTransactionReceipt({
            // we can cast it because we throw earlier if we don't have one
            hash: txHash as unknown as `0x${string}`,
          })
          console.timeEnd(`txHash ${txHash}`)

          return res.data
        },
      }),
      [isSetup, setIsModalOpen, address, client, balance],
    )
  }

  return wagmiWriteContract
}
